import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRepresentative } from "../../api/queries/representatives/useRepresentative";
import { useCreateRepresentative } from "../../api/queries/representatives/useCreateRepresentative";
import {
  CreateRepresentativeContactPayload,
  CreateRepresentativePayload,
  UpdateRepresentativeContactPayload,
  UpdateRepresentativePayload,
} from "../../api/types/representative";
import { useUpdateRepresentative } from "../../api/queries/representatives/useUpdateRepresentative";
import { useDeleteRepresentative } from "../../api/queries/representatives/useDeleteRepresentative";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { useApproveRepresentative } from "../../api/queries/representatives/useApproveRepresentative";
import { appToastContext } from "../../context/toast";
import { useContactPlatforms } from "../../api/queries/enum/useContactPlatforms";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useInstitutions } from "../../api/queries/institutions/useInstitutions";
import { Panel } from "primereact/panel";
import { userContext } from "../../context/user";
import { Message } from "primereact/message";
import { CONTACT_PLATFORM_CODE } from "../../api/types/enum";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";
import { useCountryStates } from "../../api/queries/enum/useCountryStates";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useCountries } from "../../api/queries/enum/useCountries";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { useChecklists } from "../../api/queries/checklists/useChecklists";
import { useCreateInstitution } from "../../api/queries/institutions/useCreateInstitution";
import { CreateInstitutionPayload } from "../../api/types/institution";

export default function RepresentaiveForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ref: appToastRef } = useContext(appToastContext);
  const { isAdmin } = useContext(userContext);

  const createRepresentative = useCreateRepresentative();
  const deleteRepresentative = useDeleteRepresentative();
  const updateRepresentative = useUpdateRepresentative(uuid);
  const approveRepresentative = useApproveRepresentative(uuid);
  const createInstituon = useCreateInstitution();
  const { data: representativeResponse } = useRepresentative(uuid);
  const { data: contactPlatformOptions } = useContactPlatforms();
  const { data: institutionOptions } = useInstitutions({
    limit: 100,
    offset: 0,
  });

  const contactPlatformWorkEmail = contactPlatformOptions?.data.results.find(
    (platform) => platform.code === CONTACT_PLATFORM_CODE.WORK_EMAIL,
  );

  const contactPlatformWorkPhone = contactPlatformOptions?.data.results.find(
    (platform) => platform.code === CONTACT_PLATFORM_CODE.WORK_PHONE,
  );

  const contactPlatformLinkedin = contactPlatformOptions?.data.results.find(
    (platform) => platform.code === CONTACT_PLATFORM_CODE.LINKEDIN,
  );

  const [firstName, setFirstName] = useState<string>("");
  const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setFirstName(event.target.value);

  const [lastName, setLastName] = useState<string>("");
  const onLastNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setLastName(event.target.value);

  const [email, setEmail] = useState<ContactFormValue>({
    id: undefined,
    value: "",
  });
  const onEmailChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setEmail(Object.assign({}, email, { value: event.target.value }));

  const [phone, setPhone] = useState<ContactFormValue>({
    id: undefined,
    value: "",
  });
  const onPhoneChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setPhone(Object.assign({}, phone, { value: event.target.value }));

  const [linkedin, setLinkedin] = useState<ContactFormValue>({
    id: undefined,
    value: "",
  });
  const onWebChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setLinkedin(Object.assign({}, linkedin, { value: event.target.value }));

  const [position, setPosition] = useState<string>("");
  const onPositionChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setPosition(event.target.value);

  const [institution, setInstitution] = useState<string>("");
  const onInstitutionChange = (event: DropdownChangeEvent) =>
    setInstitution(event.value);

  const institutionTypes: InstitutionType[] = [
    { label: "Select existing", value: "existing" },
    { label: "Create new", value: "new" },
  ];
  const [institutionType, setInstitutionType] = useState<string>(
    institutionTypeValue.EXISTING,
  );

  const onInstitutionTypeChange = (event: SelectButtonChangeEvent) =>
    setInstitutionType(event.value);

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [web, setWeb] = useState<string>("");
  const handleWebChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setWeb(event.target.value);

  const { data: countryOptions } = useCountries();

  const [country, setCountry] = useState<string>("");
  const onCountryChange = (event: DropdownChangeEvent) =>
    setCountry(event.value);

  const { data: countryStateOptions } = useCountryStates({
    limit: 100,
    offset: 0,
    fCountryId: country,
  });

  const [state, setState] = useState<string>("");
  const onStateChange = (event: DropdownChangeEvent) => setState(event.value);

  const [city, setCity] = useState<string>("");
  const onCityChange = (event: ChangeEvent<HTMLInputElement>) =>
    setCity(event.target.value);

  const { data: journeyTypeOptions } = useJourneyTypes();

  const [context, setContext] = useState<string[]>([]);
  const onContextChange = (event: MultiSelectChangeEvent) =>
    setContext(event.value);

  const { data: checklistOptions } = useChecklists({ limit: 100, offset: 0 });

  const [checklists, setChecklists] = useState<string[]>([]);
  const onChecklistChange = (event: DropdownChangeEvent) =>
    setChecklists(event.value);

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();

    let institutionId = institution;

    if (institutionType === institutionTypeValue.NEW) {
      const payload: CreateInstitutionPayload = {
        name,
        country,
        city,
        state,
        web,
        context,
        checklists,
      };
      const { data } = await createInstituon.mutateAsync(payload);

      institutionId = data.id;

      queryClient.invalidateQueries(queryKeys.institutionList(), {
        refetchInactive: true,
      });
    }

    if (uuid) {
      const contacts: UpdateRepresentativeContactPayload[] = [];

      if (email.value) {
        contacts.push({
          type_id: contactPlatformWorkEmail?.id || "",
          value: email.value,
          id: email.id || null,
        });
      }

      if (phone.value) {
        contacts.push({
          type_id: contactPlatformWorkPhone?.id || "",
          value: phone.value,
          id: phone.id || null,
        });
      }

      if (linkedin.value) {
        contacts.push({
          type_id: contactPlatformLinkedin?.id || "",
          value: linkedin.value,
          id: linkedin.id || null,
        });
      }

      const payload: UpdateRepresentativePayload = {
        first_name: firstName,
        last_name: lastName,
        institution: { id: institutionId, position },
        contacts,
      };

      await updateRepresentative.mutateAsync(payload);
      queryClient.invalidateQueries(queryKeys.representative(uuid));
    } else {
      const contacts: CreateRepresentativeContactPayload[] = [];

      if (email.value) {
        contacts.push({
          type_id: contactPlatformWorkEmail?.id || "",
          value: email.value,
        });
      }

      if (phone.value) {
        contacts.push({
          type_id: contactPlatformWorkPhone?.id || "",
          value: phone.value,
        });
      }

      if (linkedin.value) {
        contacts.push({
          type_id: contactPlatformLinkedin?.id || "",
          value: linkedin.value,
        });
      }

      const payload: CreateRepresentativePayload = {
        first_name: firstName,
        last_name: lastName,
        institution: { id: institutionId, position },
        contacts,
      };

      await createRepresentative.mutateAsync(payload);
    }

    queryClient.invalidateQueries(queryKeys.representativeList(), {
      refetchInactive: true,
    });

    navigate("/representatives");
  };

  useEffect(() => {
    if (representativeResponse) {
      const { data: representative } = representativeResponse;

      setFirstName(representative.first_name);
      setLastName(representative.last_name);

      if (representative.positions.length > 0) {
        setInstitution(representative.positions[0].institution.id);
        setPosition(representative.positions[0].position);
      }

      const workEmail = representative.contacts.find(
        (contact) =>
          contact.contact_platform.id === contactPlatformWorkEmail?.id,
      );

      const workPhone = representative.contacts.find(
        (contact) =>
          contact.contact_platform.id === contactPlatformWorkPhone?.id,
      );

      const website = representative.contacts.find(
        (contact) =>
          contact.contact_platform.id === contactPlatformLinkedin?.id,
      );

      if (workEmail) {
        setEmail({
          id: workEmail.id,
          value: workEmail.contact_value,
        });
      }

      if (workPhone) {
        setPhone({
          id: workPhone.id,
          value: workPhone.contact_value,
        });
      }

      if (website) {
        setLinkedin({
          id: website.id,
          value: website.contact_value,
        });
      }
    }
  }, [
    representativeResponse,
    contactPlatformOptions,
    contactPlatformWorkEmail,
    contactPlatformWorkPhone,
    contactPlatformLinkedin,
  ]);

  const onApprove = async () => {
    await approveRepresentative.mutateAsync();

    queryClient.invalidateQueries(queryKeys.representative(uuid));
    queryClient.invalidateQueries(queryKeys.representativeList(), {
      refetchInactive: true,
    });

    appToastRef?.current?.show({
      summary: "Representative approved.",
      severity: "success",
    });
  };

  const onDelete = async () => {
    await deleteRepresentative.mutateAsync(uuid);

    queryClient.invalidateQueries(queryKeys.representative(uuid));
    queryClient.invalidateQueries(queryKeys.representativeList(), {
      refetchInactive: true,
    });

    navigate("/representatives");
  };

  const onCancel = () => navigate("/representatives");

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="Representative" toggleable={true} className="mb-3">
        <div className="p-fluid">
          {isAdmin() && uuid && !representativeResponse?.data.is_approved && (
            <Message
              severity="warn"
              text="This representative needs to be approved."
              className="mb-3"
            />
          )}

          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <InputText value={firstName} onChange={onFirstNameChange} />
                <label>First Name *</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <InputText value={lastName} onChange={onLastNameChange} />
                <label>Last Name *</label>
              </div>
            </div>
          </div>

          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <InputText value={position} onChange={onPositionChange} />
                <label>Job Position *</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <InputText value={email.value} onChange={onEmailChange} />
                <label>E-mail Address *</label>
              </div>
            </div>
          </div>

          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <InputText value={phone.value} onChange={onPhoneChange} />
                <label>Phone number</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <InputText value={linkedin.value} onChange={onWebChange} />
                <label>LinkedIn</label>
              </div>
            </div>
          </div>
        </div>
      </Panel>

      <Panel header="Institution" toggleable={true} className="mb-3">
        <div className="grid">
          <div className="field col">
            <SelectButton
              options={institutionTypes}
              value={institutionType}
              onChange={onInstitutionTypeChange}
              className="p-button-sm"
            />
          </div>
        </div>

        {institutionType === institutionTypeValue.EXISTING && (
          <div className="p-fluid">
            <div className="grid formgrid pt-3">
              <div className="field col">
                <div className="p-float-label">
                  <Dropdown
                    options={institutionOptions?.data.results || []}
                    value={institution}
                    onChange={onInstitutionChange}
                    optionValue="id"
                    optionLabel="name"
                  />
                  <label>Select Institution *</label>
                </div>
              </div>
            </div>
          </div>
        )}

        {institutionType === institutionTypeValue.NEW && (
          <div className="p-fluid">
            <div className="field pt-3">
              <div className="p-float-label">
                <InputText value={name} onChange={onNameChange} />
                <label>Institution name *</label>
              </div>
            </div>

            <div className="grid formgrid pt-3">
              <div className="field col">
                <div className="p-float-label">
                  <Dropdown
                    options={countryOptions?.data.results || []}
                    value={country}
                    onChange={onCountryChange}
                    optionLabel="name"
                    optionValue="id"
                  />
                  <label>Country *</label>
                </div>
              </div>

              <div className="field col">
                <div className="p-float-label">
                  <InputText value={city} onChange={onCityChange} />
                  <label>City *</label>
                </div>
              </div>

              <div className="field col">
                <div className="p-float-label">
                  <Dropdown
                    options={countryStateOptions?.data.results || []}
                    value={state}
                    onChange={onStateChange}
                    optionLabel="name"
                    optionValue="id"
                    disabled={!country}
                  />
                  <label>State *</label>
                </div>
              </div>
            </div>

            <div className="field pt-3">
              <div className="p-float-label">
                <InputText value={web} onChange={handleWebChange} />
                <label>Web *</label>
              </div>
            </div>

            <div className="field pt-3">
              <div className="p-float-label">
                <MultiSelect
                  options={journeyTypeOptions?.data.results || []}
                  value={context}
                  onChange={onContextChange}
                  optionLabel="name"
                  optionValue="id"
                />
                <label>Context *</label>
              </div>
            </div>

            <div className="field pt-3">
              <div className="p-float-label">
                <MultiSelect
                  options={checklistOptions?.data.results || []}
                  value={checklists}
                  onChange={onChecklistChange}
                  optionLabel="name"
                  optionValue="id"
                />
                <label>Checklists *</label>
              </div>
            </div>
          </div>
        )}
      </Panel>

      <div className="flex gap-1 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-sm p-button-secondary"
          text={true}
          onClick={onCancel}
        />

        {uuid && (
          <>
            {isAdmin() && !representativeResponse?.data.is_approved && (
              <Button
                type="button"
                label="Approve"
                className="p-button-sm p-button-success"
                onClick={onApprove}
              />
            )}

            <Button
              type="button"
              label="Delete"
              outlined={true}
              className="p-button-sm p-button-danger"
              onClick={onDelete}
            />
          </>
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
        />
      </div>
    </form>
  );
}

interface ContactFormValue {
  value: string;
  id: string | undefined;
}

const institutionTypeValue = {
  NEW: "new",
  EXISTING: "existing",
};

interface InstitutionType {
  label: string;
  value: string;
}
